"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Phone } from "lucide-react";
import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { Marketing360Form } from "./Marketing360Form";

const FORM_TITLE = "Request A Consultation Today!";
const SHOW_TEXT_DURATION = 3000;
const DELAY_HIDE_TEXT_DURATION = 300;

function FloatingContactButton() {
  const [showText, setShowText] = React.useState(true);
  const [dialogIsOpen, setDialogIsOpen] = React.useState(false);
  const timeoutRef = React.useRef<number | null>(null);

  const handleOnMouseEnter = React.useCallback(() => {
    setShowText(true);
  }, []);

  const handleOnMouseLeave = React.useCallback(() => {
    setTimeout(() => {
      setShowText(false);
    }, DELAY_HIDE_TEXT_DURATION);
  }, []);

  const handleOnOpenChange = React.useCallback(() => {
    setDialogIsOpen(value => !value);
  }, []);

  const handleOnKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      handleOnOpenChange();
    },
    [handleOnOpenChange],
  );

  /*
   * Handles showing the button text on render
   */
  React.useEffect(() => {
    timeoutRef.current = window.setTimeout(() => {
      setShowText(false);
    }, SHOW_TEXT_DURATION);

    return () => {
      if (timeoutRef.current === null) {
        return;
      }

      window.clearTimeout(timeoutRef.current);
    };
  });

  // Adds the functionality to close the dialog with `ESC`
  // if its open
  React.useEffect(() => {
    if (dialogIsOpen) {
      document.addEventListener("keydown", handleOnKeyDown);
    } else {
      document.removeEventListener("keydown", handleOnKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleOnKeyDown);
    };
  }, [dialogIsOpen, handleOnKeyDown]);

  return (
    <>
      <Dialog open={dialogIsOpen} onOpenChange={handleOnOpenChange}>
        <DialogContent className="bg-card max-w-2xl">
          <DialogHeader>
            <DialogTitle>{FORM_TITLE}</DialogTitle>
          </DialogHeader>

          <Marketing360Form />
        </DialogContent>
      </Dialog>

      <motion.button
        layout
        className="rounded-full gap-2 fixed bottom-[25px] right-[25px] z-50 hidden md:flex flex-row bg-primary text-primary-foreground p-5 shadow-xl ring-4 ring-primary/20"
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        onClick={handleOnOpenChange}
      >
        <Phone size={28} />

        <AnimatePresence>
          {showText && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              Contact Us
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </>
  );
}

export default FloatingContactButton;
